import * as React from "react"
import { money } from "../format"
import type { Addon } from "../types"

export function AddonCard({ addon, selected, onToggle, onImageExpand }: { addon: Addon; selected: boolean; onToggle: () => void; onImageExpand: () => void }) {
    const [galleryOpen, setGalleryOpen] = React.useState(false)
    const [activeImage, setActiveImage] = React.useState(0)
    const galleryTriggerRef = React.useRef<HTMLButtonElement | null>(null)
    const galleryCloseRef = React.useRef<HTMLButtonElement | null>(null)
    const galleryRef = React.useRef<HTMLDivElement | null>(null)
    const images = addon.images && addon.images.length > 0 ? addon.images.slice(0, 3) : [addon.image]
    const openGallery = () => {
        setActiveImage(0)
        setGalleryOpen(true)
        onImageExpand()
    }
    React.useEffect(() => {
        if (!galleryOpen) return
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = "hidden"
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setGalleryOpen(false)
            if (event.key === "ArrowLeft" && images.length > 1) setActiveImage((current) => (current - 1 + images.length) % images.length)
            if (event.key === "ArrowRight" && images.length > 1) setActiveImage((current) => (current + 1) % images.length)
            if (event.key === "Tab") {
                const controls = Array.from(galleryRef.current?.querySelectorAll<HTMLButtonElement>("button:not([disabled])") ?? [])
                const first = controls[0]
                const last = controls[controls.length - 1]
                if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
                if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
            }
        }
        window.addEventListener("keydown", handleKeyDown)
        window.requestAnimationFrame(() => galleryCloseRef.current?.focus())
        return () => {
            document.body.style.overflow = previousOverflow
            window.removeEventListener("keydown", handleKeyDown)
            window.requestAnimationFrame(() => galleryTriggerRef.current?.focus())
        }
    }, [galleryOpen, images.length])
    return <>
        <article className={`ah-addon-card ${selected ? "is-selected" : ""}`}>
            <button
                ref={galleryTriggerRef}
                type="button"
                className="ah-addon-image-button"
                aria-haspopup="dialog"
                aria-label={`View ${addon.name} images (${images.length})`}
                onClick={openGallery}
            >
                <img src={images[0]} alt="" />
                <span aria-hidden="true">
                    <svg viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="5.5" /><path d="m15 15 4 4M10.5 8v5M8 10.5h5" /></svg>
                    {images.length}
                </span>
            </button>
            <div className="ah-addon-copy">
                <h3>{addon.name}</h3>
                {addon.description && <p>{addon.description}</p>}
            </div>
            <button
                type="button"
                className="ah-addon-toggle-button"
                aria-pressed={selected}
                aria-label={selected ? `Remove ${addon.name} from booking` : `Add ${addon.name} to booking for ${money(addon.pricePence)}`}
                onClick={onToggle}
            >
                {selected ? `✓ Added · ${money(addon.pricePence)}` : `Add for ${money(addon.pricePence)}`}
            </button>
        </article>
        {galleryOpen && <div ref={galleryRef} className="ah-addon-gallery" role="dialog" aria-modal="true" aria-label={`${addon.name} image gallery`} onMouseDown={(event) => { if (event.target === event.currentTarget) setGalleryOpen(false) }}>
            <header>
                <strong>{addon.name}</strong>
                <button ref={galleryCloseRef} type="button" aria-label="Close image gallery" onClick={() => setGalleryOpen(false)}>×</button>
            </header>
            <div className="ah-addon-gallery-stage">
                <button type="button" aria-label="Previous image" disabled={images.length < 2} onClick={() => setActiveImage((current) => (current - 1 + images.length) % images.length)}>‹</button>
                <figure>
                    <img src={images[activeImage]} alt={`${addon.name}, image ${activeImage + 1} of ${images.length}`} />
                    <figcaption aria-live="polite">{activeImage + 1} of {images.length}</figcaption>
                </figure>
                <button type="button" aria-label="Next image" disabled={images.length < 2} onClick={() => setActiveImage((current) => (current + 1) % images.length)}>›</button>
            </div>
            {images.length > 1 && <div className="ah-addon-gallery-thumbnails" aria-label="Choose image">
                {images.map((image, index) => <button key={image} type="button" className={index === activeImage ? "is-active" : ""} aria-label={`Show image ${index + 1} of ${images.length}`} aria-current={index === activeImage ? "true" : undefined} onClick={() => setActiveImage(index)}><img src={image} alt="" /></button>)}
            </div>}
        </div>}
    </>
}
